import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { query, orderBy, onSnapshot } from 'firebase/firestore'
import SearchBox from '@common/src/common/inputbox/searchBox'

const getNestedValue = (obj, path) => path?.split('.').reduce((acc, part) => acc?.[part], obj)

// `term` must already be trimmed + lowercased (see normalizedSearchTerm below) —
// an untrimmed term of just whitespace would otherwise match every row, since
// `${firstName} ${lastName}` always contains a literal space from the join.
const matchesSearch = (row, searchFields, term) => {
  if (!term) return true
  const fullName = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase()
  if (fullName.includes(term)) return true
  return searchFields.some((field) => {
    const value = getNestedValue(row, field)
    return typeof value === 'string' && value.toLowerCase().includes(term)
  })
}

// Realtime, fully client-side counterpart to TableList: subscribes to the whole
// (already filtered by dbRef) collection instead of cursor-paginating it, so search
// can match any field locally. Only safe for collections expected to stay small
// (a few hundred docs) — do not point this at a large/unbounded collection.
const LocalSearchTableList = forwardRef(
  (
    {
      name,
      columns,
      dbRef,
      tools = {},
      pagination = {},
      searchTerm = '',
      searchFields = [],
      searchInputValue,
      onSearchInputChange,
      searchPlaceholder = 'Search...',
      rowFilter,
      toolbar,
    },
    ref
  ) => {
    const [allData, setAllData] = useState([])
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const pageSize = pagination.pageSize || 10

    useEffect(() => {
      if (!dbRef) return
      setLoading(true)
      const unsubscribe = onSnapshot(query(dbRef, orderBy('createdAt', 'desc')), (snapshot) => {
        setAllData(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })))
        setLoading(false)
      })
      return () => unsubscribe()
    }, [dbRef])

    const normalizedSearchTerm = useMemo(() => (searchTerm || '').trim().toLowerCase(), [searchTerm])

    useEffect(() => {
      setCurrentPage(1)
    }, [normalizedSearchTerm, rowFilter])

    const filteredData = useMemo(
      () =>
        allData.filter(
          (row) => (!rowFilter || rowFilter(row)) && matchesSearch(row, searchFields, normalizedSearchTerm)
        ),
      [allData, rowFilter, searchFields, normalizedSearchTerm]
    )

    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize))
    const pageData = useMemo(
      () => filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize),
      [filteredData, currentPage, pageSize]
    )

    useImperativeHandle(ref, () => ({
      refreshCurrentPage: () => setCurrentPage(1),
    }))

    const handlePageChange = (direction) => {
      if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1)
      if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1)
    }

    const renderColumns = useMemo(
      () => [
        <th key="serial-number">Sl.No</th>,
        ...(columns?.map((column) => <th key={column.key}>{column.title}</th>) || []),
      ],
      [columns]
    )

    const renderRows = useMemo(
      () =>
        pageData.map((item, index) => (
          <tr key={item.id || index} className="product-list invoice-list border border-defaultborder dark:border-defaultborder/10">
            <td>{(currentPage - 1) * pageSize + index + 1}</td>
            {columns.map((col, colIndex) => (
              <td key={`${col.key}-${colIndex}`} className={col.key === 'actions' ? 'gap-2' : ''}>
                {col.render ? col.render(getNestedValue(item, col.dataIndex), item) : getNestedValue(item, col.dataIndex)}
              </td>
            ))}
          </tr>
        )),
      [pageData, columns, currentPage, pageSize]
    )

    const iconClass = tools.icon || 'ri-add-line'
    const isSearching = Boolean(normalizedSearchTerm)
    const showSearchBox = onSearchInputChange != null

    return (
      <div className="grid grid-cols-12 gap-6">
        <div className="xl:col-span-12 col-span-12">
          <div className="box">
            <div className="box-header justify-between items-center flex-wrap gap-3">
              <div className="box-title">{name}</div>
              {toolbar}
              {showSearchBox && (
                <div className="flex flex-1 justify-end max-w-xs">
                  <SearchBox
                    classes="py-2 px-3 !ti-form-input !rounded-md !focus:z-10"
                    width="w-full"
                    placeHolder={searchPlaceholder}
                    value={searchInputValue}
                    onChange={onSearchInputChange}
                  />
                </div>
              )}
              {tools.btnName && (
                <div className="flex">
                  <button
                    type="button"
                    onClick={tools.btnClick}
                    className="ti-btn !py-1 !px-2 !text-[0.75rem] !text-white !font-medium bg-primary h-10"
                  >
                    <i className={`${iconClass} font-semibold align-middle me-1`} /> {tools.btnName}
                  </button>
                </div>
              )}
            </div>
            <div className="box-body">
              <div className="table-responsive mb-4">
                <table className="table whitespace-nowrap table-bordered min-w-full">
                  <thead>
                    <tr className="border-b">{renderColumns}</tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={columns.length + 1} className="text-center">
                          Loading...
                        </td>
                      </tr>
                    )}
                    {!loading && pageData.length === 0 && (
                      <tr>
                        <td colSpan={columns.length + 1} className="text-center">
                          <div className="alert alert-info">{isSearching ? 'No results found.' : 'No data available'}</div>
                        </td>
                      </tr>
                    )}
                    {!loading && renderRows}
                  </tbody>
                </table>
              </div>
              <nav className="sm:flex items-center justify-between flex-wrap">
                <ul className="ti-pagination mb-0 flex flex-row rounded-sm text-[1rem] !ps-0">
                  <li className="page-item">
                    <Link
                      className="page-link !text-primary !py-[0.375rem] !px-[0.75rem]"
                      to="#"
                      onClick={() => handlePageChange('prev')}
                      style={currentPage <= 1 || loading ? { pointerEvents: 'none' } : null}
                    >
                      Previous
                    </Link>
                  </li>
                  <Link className="page-link !text-primary !py-[0.375rem] !px-[0.75rem]" to="#">
                    {currentPage}
                  </Link>
                  <li className="page-item">
                    <Link
                      className="page-link !text-primary !py-[0.375rem] !px-[0.75rem]"
                      to="#"
                      onClick={() => handlePageChange('next')}
                      style={currentPage >= totalPages || loading ? { pointerEvents: 'none' } : null}
                    >
                      Next
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

LocalSearchTableList.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.any).isRequired,
  name: PropTypes.string.isRequired,
  dbRef: PropTypes.object.isRequired,
  pagination: PropTypes.shape({
    pageSize: PropTypes.number,
  }),
  tools: PropTypes.shape({
    btnName: PropTypes.string,
    btnIcon: PropTypes.string,
    btnClick: PropTypes.func,
  }),
  searchTerm: PropTypes.string,
  searchFields: PropTypes.arrayOf(PropTypes.string),
  searchInputValue: PropTypes.string,
  onSearchInputChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  // Extra client-side filter for conditions Firestore can't query (e.g. a missing field).
  rowFilter: PropTypes.func,
  // Extra controls shown in the header, before the search box.
  toolbar: PropTypes.node,
}

export default LocalSearchTableList
